import { queryClient } from "@/services/react-query";
import { getUserInfo, updateUserInfo } from "@/services/users";
import { UserInfo } from "@/services/users/types/user.types";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLoader } from "./LoaderContext";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "@/firebase.config";
import { useQuery } from "react-query";
import { resetTokenRefresh } from "@/services/api";
import { clearPdcEmbedStorage, ensurePdcEmbedApiKey } from "@/utils/pdcEmbed";
import {
  clearPendingPhoneValidatedSyncFlag,
  firebaseUserShowsVerifiedPhoneMatch,
  hasPendingPhoneValidatedSyncFlag,
} from "@/utils/phoneValidatedSync";

type UserContext = {
  userInfo?: UserInfo;
  isLoadingUser: boolean;
  isAuthenticated?: boolean;
  logOut: () => void;
  refetchUserInfo: () => Promise<void>;
};

const UserContext = createContext<UserContext>({} as UserContext);

type UserProviderProps = {
  children: React.ReactNode;
};

export const UserProvider = ({ children }: UserProviderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const loader = useLoader();
  const phoneValidatedSyncInFlight = useRef(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>();
  const [currentUserUid, setCurrentUserUid] = useState<string | null>(null);

  useEffect(() => {
    loader.show("authState");

    const unsubscribe = onAuthStateChanged(auth, async (_user) => {
      if (_user) {
        setCurrentUserUid(_user.uid);
        setIsAuthenticated(true);
        loader.show("userInfo");
        // Não chamar invalidateQueries aqui: o useQuery abaixo já roda GET /users/me quando
        // `enabled` vira true — invalidar no mesmo tick gerava 2× GET /me por login.
      } else {
        clearPdcEmbedStorage();
        setCurrentUserUid(null);
        setIsAuthenticated(false);
      }
      loader.hide("authState");
    });

    return () => unsubscribe();
  }, []);

  const { data: userInfo, isLoading: isLoadingUser, isFetching: isFetchingUser } =
    useQuery(
      ["user-info", isAuthenticated, currentUserUid],
      () => getUserInfo(),
      {
        enabled: isAuthenticated === true && currentUserUid != null,
        retry: 1,
        staleTime: 120_000,
        refetchOnWindowFocus: false,
        /** Evita novo GET /users/me em todo remount de rota quando o cache ainda está fresco. */
        refetchOnMount: false,
        onSettled() {
          loader.hide("userInfo");
        },
      }
    );

  const logOut = useCallback(async () => {
    clearPdcEmbedStorage();
    setIsAuthenticated(false);
    setCurrentUserUid(null);
    navigate("/sign-in");

    await auth.signOut();
    queryClient.invalidateQueries({ queryKey: ["user-info"] });
    await queryClient.cancelQueries();
    // Limpar completamente o cache para evitar dados do usuário anterior
    queryClient.clear();
    // Reset flag do token para forçar refresh no próximo login
    resetTokenRefresh();
  }, [navigate]);

  // PDC iframe: refresh masked `x-api-key` in localStorage on every successful login / user load
  useEffect(() => {
    if (isAuthenticated !== true || userInfo?.id == null) return;
    ensurePdcEmbedApiKey(userInfo.id);
  }, [isAuthenticated, userInfo?.id]);

  /**
   * If OTP succeeded but `PUT /users/me` failed (or login never called it), keep DB `phoneNumberValidated`
   * in sync when the user navigates. Uses sessionStorage (set on OTP success) and Firebase session hints.
   */
  useEffect(() => {
    if (isAuthenticated !== true || userInfo?.id == null) return;
    if (userInfo.phoneNumberValidated) {
      clearPendingPhoneValidatedSyncFlag();
      return;
    }
    if (isLoadingUser || isFetchingUser) return;

    const fb = auth.currentUser;
    if (!fb) return;

    const pendingFlag = hasPendingPhoneValidatedSyncFlag();
    const firebaseMatch = firebaseUserShowsVerifiedPhoneMatch(fb, userInfo.phoneNumber);
    if (!pendingFlag && !firebaseMatch) return;

    if (phoneValidatedSyncInFlight.current) return;
    phoneValidatedSyncInFlight.current = true;

    void (async () => {
      try {
        await updateUserInfo({ phoneNumberValidated: true });
        clearPendingPhoneValidatedSyncFlag();
        await queryClient.invalidateQueries({ queryKey: ["user-info"], exact: false });
      } catch {
        /* leave pending flag / rely on next navigation or manual refetch */
      } finally {
        phoneValidatedSyncInFlight.current = false;
      }
    })();
  }, [
    isAuthenticated,
    userInfo?.id,
    userInfo?.phoneNumber,
    userInfo?.phoneNumberValidated,
    isLoadingUser,
    isFetchingUser,
    location.pathname,
  ]);

  // Firebase session exists but backend user was removed (e.g. admin hard reset) — avoid stuck UI/loaders
  useEffect(() => {
    if (isAuthenticated !== true) return;
    if (isLoadingUser || isFetchingUser) return;
    if (userInfo?.id != null) return;
    void logOut();
  }, [
    isAuthenticated,
    isLoadingUser,
    isFetchingUser,
    userInfo,
    logOut,
  ]);

  const refetchUserInfo = useCallback(async () => {
    await queryClient.refetchQueries({
      queryKey: ["user-info"],
      exact: false,
    });
  }, []);

  return (
    <UserContext.Provider
      value={{
        userInfo,
        isLoadingUser,
        isAuthenticated,
        logOut,
        refetchUserInfo,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContext => useContext(UserContext);
